/**
 * Best-effort browser/OS/screen detection for the Web Player.
 *
 * Used for two purposes:
 *  1. The pairing screen's human-readable hint ("Edge on Windows ·
 *     1920×1080") so an admin can tell which physical screen is which
 *     before claiming the code.
 *  2. A compact `platform` string sent on /device/pair/start.
 *
 * Deliberately dependency-free and defensive: every modern signage
 * browser (Edge, Chrome, webOS, Tizen) differs in what it exposes, and
 * nothing here may throw — a failed detection just yields "Unknown".
 * We intentionally avoid deprecated APIs (navigator.platform is only a
 * last-resort fallback behind UA-CH / userAgent parsing).
 */

export interface DeviceMeta {
  os: string;
  browser: string;
  browserVersion: string;
  screenWidth: number;
  screenHeight: number;
  resolution: string;
  pixelRatio: number;
  orientation: "landscape" | "portrait";
}

/**
 * Snapshot shape sent on the heartbeat. Field names match the backend
 * heartbeat validation (DeviceApiController::heartbeat) exactly. All keys
 * are optional server-side; we only send the ones we could detect.
 */
export interface BrowserHeartbeatMeta {
  os?: string;
  browser?: string;
  browser_version?: string;
  screen_width?: number;
  screen_height?: number;
  orientation?: "landscape" | "portrait";
  device_pixel_ratio?: number;
}

function detectOs(ua: string): string {
  if (/Windows NT/i.test(ua)) return "Windows";
  if (/CrOS/i.test(ua)) return "ChromeOS";
  if (/Android/i.test(ua)) return "Android";
  if (/Web0?S|webOS/i.test(ua)) return "webOS";
  if (/Tizen/i.test(ua)) return "Tizen";
  if (/Mac OS X/i.test(ua)) return "macOS";
  if (/Linux/i.test(ua)) return "Linux";
  return "Unknown";
}

function detectBrowser(ua: string): { name: string; version: string } {
  // Order matters: Edge/Opera/Samsung masquerade as Chrome, so test the
  // more specific tokens first.
  const tests: Array<{ name: string; re: RegExp }> = [
    { name: "Edge", re: /Edg(?:e|A|iOS)?\/([\d.]+)/ },
    { name: "Samsung Internet", re: /SamsungBrowser\/([\d.]+)/ },
    { name: "Opera", re: /OPR\/([\d.]+)/ },
    { name: "Firefox", re: /Firefox\/([\d.]+)/ },
    { name: "Chrome", re: /Chrome\/([\d.]+)/ },
    { name: "Safari", re: /Version\/([\d.]+).*Safari/ },
  ];
  for (const t of tests) {
    const m = ua.match(t.re);
    if (m) return { name: t.name, version: m[1] };
  }
  return { name: "Unknown", version: "" };
}

export function detectDeviceMeta(): DeviceMeta {
  let ua = "";
  try { ua = navigator.userAgent || ""; } catch { /* sandboxed */ }

  const { name, version } = detectBrowser(ua);
  let w = 0;
  let h = 0;
  let ratio = 1;
  try {
    // screen.width/height reports the physical panel; fall back to the
    // viewport if the platform blocks it.
    w = (window.screen && window.screen.width) || window.innerWidth || 0;
    h = (window.screen && window.screen.height) || window.innerHeight || 0;
    ratio = window.devicePixelRatio || 1;
  } catch { /* leave zeros */ }

  return {
    os: detectOs(ua),
    browser: name,
    browserVersion: version,
    screenWidth: w,
    screenHeight: h,
    resolution: w && h ? `${w}×${h}` : "Unknown",
    pixelRatio: Math.round(ratio * 100) / 100,
    orientation: w >= h ? "landscape" : "portrait",
  };
}

/**
 * Build the heartbeat metadata payload from a DeviceMeta, omitting values we
 * couldn't reliably detect so the server stores null rather than a guess.
 */
export function toHeartbeatMeta(meta: DeviceMeta): BrowserHeartbeatMeta {
  const out: BrowserHeartbeatMeta = {};
  if (meta.os && meta.os !== "Unknown") out.os = meta.os;
  if (meta.browser && meta.browser !== "Unknown") out.browser = meta.browser;
  if (meta.browserVersion) out.browser_version = meta.browserVersion;
  if (meta.screenWidth > 0) out.screen_width = meta.screenWidth;
  if (meta.screenHeight > 0) out.screen_height = meta.screenHeight;
  out.orientation = meta.orientation;
  if (meta.pixelRatio > 0) out.device_pixel_ratio = meta.pixelRatio;
  return out;
}

/** Compact, log-friendly platform string, e.g. "web; Edge 139; Windows; 1920×1080". */
export function platformString(meta: DeviceMeta): string {
  const parts = [
    "web",
    meta.browser !== "Unknown"
      ? `${meta.browser}${meta.browserVersion ? " " + meta.browserVersion.split(".")[0] : ""}`
      : null,
    meta.os !== "Unknown" ? meta.os : null,
    meta.resolution !== "Unknown" ? meta.resolution : null,
  ].filter(Boolean);
  // Server stores platform in a 64-char column — keep it short.
  return parts.join("; ").slice(0, 64);
}

/** One-line human label for the pairing screen hint. */
export function humanLabel(meta: DeviceMeta): string {
  const browser = meta.browser !== "Unknown"
    ? `${meta.browser}${meta.browserVersion ? " " + meta.browserVersion.split(".")[0] : ""}`
    : "Browser";
  const os = meta.os !== "Unknown" ? ` on ${meta.os}` : "";
  const res = meta.resolution !== "Unknown" ? ` · ${meta.resolution}` : "";
  return `${browser}${os}${res}`;
}
